import { useQuery } from '@tanstack/react-query';
import { operationRequestsApi } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type { PendingApprovalCount } from '../api/types';
import { useStoreContext } from '../context/StoreContext';

/**
 * Admin view of "which store is waiting" — the PENDING seller-submission count
 * per store, plus a total for the nav badge. The total follows the global
 * store context (SPEC §5): with a store selected it counts only that store.
 * Shares the `['operationRequests', …]` prefix, so any approve/reject
 * invalidation refreshes it too.
 */
export function usePendingApprovals(
  enabled = true,
): { total: number; byStore: PendingApprovalCount[] } {
  const query = useQuery({
    queryKey: queryKeys.pendingApprovalCounts,
    queryFn: () => operationRequestsApi.pendingCounts(),
    // The counts endpoint is admin-only — never fire it for a SELLER.
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { storeId, storeIds } = useStoreContext();
  const byStore = query.data ?? [];
  const scoped = storeId
    ? byStore.filter((s) => s.storeId === storeId)
    : storeIds
      ? byStore.filter((s) => storeIds.includes(s.storeId))
      : byStore;
  return { total: scoped.reduce((sum, s) => sum + s.count, 0), byStore };
}
